export interface InfiniteSearchResultsOptions {
    afterAppend?: (root: HTMLElement) => void;
}

const initializedSearches = new WeakSet<HTMLElement>();
const PAGE_PARAMETER = 'page';

export function normalizedSearchContract(url: URL): string {
    const normalized = new URL(url.href);
    normalized.hash = '';
    normalized.searchParams.delete(PAGE_PARAMETER);
    normalized.searchParams.sort();
    return `${normalized.origin}${normalized.pathname}?${normalized.searchParams.toString()}`;
}

export function trustedNextSearchPageUrl(
    candidate: string,
    contractUrl: URL,
    currentPageUrl: URL
): URL | null {
    try {
        const next = new URL(candidate, currentPageUrl);
        if (
            next.origin !== contractUrl.origin
            || next.pathname !== contractUrl.pathname
            || normalizedSearchContract(next) !== normalizedSearchContract(contractUrl)
        ) return null;
        const currentPage = Number(currentPageUrl.searchParams.get(PAGE_PARAMETER) ?? '1');
        const nextPage = Number(next.searchParams.get(PAGE_PARAMETER));
        if (
            !Number.isSafeInteger(nextPage)
            || nextPage < 1
            || !Number.isSafeInteger(currentPage)
            || currentPage < 1
            || nextPage !== currentPage + 1
        ) return null;
        return next;
    } catch {
        return null;
    }
}

function keyedChildren(parent: Element, selector: string, keyAttribute: string): Map<string, Element> {
    const keyed = new Map<string, Element>();
    parent.querySelectorAll<Element>(selector).forEach((element) => {
        const key = element.getAttribute(keyAttribute);
        if (key) keyed.set(key, element);
    });
    return keyed;
}

function appendMissingChildren(
    currentParent: Element,
    fetchedParent: Element,
    selector: string,
    keyAttribute: string
): number {
    const current = keyedChildren(currentParent, selector, keyAttribute);
    let appended = 0;
    fetchedParent.querySelectorAll<Element>(selector).forEach((element) => {
        const key = element.getAttribute(keyAttribute);
        if (!key || current.has(key)) return;
        currentParent.append(element.cloneNode(true));
        current.set(key, element);
        appended += 1;
    });
    return appended;
}

function searchScrollRoot(root: HTMLElement, runtimeWindow: Window): Element | null {
    const candidate = root.querySelector<HTMLElement>('[data-fj-infinite-search-scroll]');
    if (!candidate) return null;
    const overflowY = runtimeWindow.getComputedStyle(candidate).overflowY;
    return overflowY === 'auto' || overflowY === 'scroll' ? candidate : null;
}

export function initializeInfiniteSearchResults(
    options: InfiniteSearchResultsOptions = {},
    documentRoot?: Document
): void {
    const documentValue = documentRoot ?? (typeof document !== 'undefined' ? document : null);
    const runtimeWindow = documentValue?.defaultView ?? (typeof window !== 'undefined' ? window : null);
    if (!documentValue || !runtimeWindow) return;

    documentValue.querySelectorAll<HTMLElement>('[data-fj-infinite-search]').forEach((root) => {
        if (initializedSearches.has(root)) return;
        initializedSearches.add(root);

        const contractValue = root.dataset.fjJobResultsUrl || runtimeWindow.location.href;
        const contractUrl = new URL(contractValue, runtimeWindow.location.href);
        let currentPageUrl = new URL(contractUrl.href);
        let observer: IntersectionObserver | null = null;
        let loading = false;

        const disconnectObserver = (): void => {
            observer?.disconnect();
            observer = null;
        };

        const bindNext = (): void => {
            disconnectObserver();
            const next = root.querySelector<HTMLAnchorElement>('[data-fj-infinite-search-next]');
            if (!next || !('IntersectionObserver' in runtimeWindow)) return;
            observer = new runtimeWindow.IntersectionObserver((entries) => {
                if (entries.some((entry) => entry.isIntersecting)) void loadNext(next);
            }, {
                root: searchScrollRoot(root, runtimeWindow),
                rootMargin: '0px 0px 240px 0px',
                threshold: 0.01,
            });
            observer.observe(next);
        };

        const loadNext = async (trigger: HTMLAnchorElement): Promise<void> => {
            if (loading) return;
            const nextUrl = trustedNextSearchPageUrl(trigger.href, contractUrl, currentPageUrl);
            if (!nextUrl) return;
            loading = true;
            trigger.setAttribute('aria-disabled', 'true');
            root.setAttribute('aria-busy', 'true');
            try {
                const response = await runtimeWindow.fetch(nextUrl.href, {
                    credentials: 'same-origin',
                    headers: { Accept: 'text/html' },
                });
                if (!response.ok) throw new Error(`Search page failed with ${response.status}`);
                const parsed = new runtimeWindow.DOMParser().parseFromString(await response.text(), 'text/html');
                const fetchedRoot = parsed.querySelector<HTMLElement>('[data-fj-infinite-search]');
                const currentItems = root.querySelector<HTMLElement>('[data-fj-infinite-search-items]');
                const fetchedItems = fetchedRoot?.querySelector<HTMLElement>('[data-fj-infinite-search-items]');
                if (!fetchedRoot || !currentItems || !fetchedItems) {
                    throw new Error('Search page did not contain the governed result contract.');
                }
                const appended = appendMissingChildren(
                    currentItems,
                    fetchedItems,
                    '[data-fj-infinite-search-item]',
                    'data-fj-infinite-search-key'
                );
                if (appended === 0) throw new Error('Search page did not advance the governed result set.');

                const currentAuxiliary = root.querySelector<HTMLElement>('[data-fj-infinite-search-auxiliary]');
                const fetchedAuxiliary = fetchedRoot.querySelector<HTMLElement>('[data-fj-infinite-search-auxiliary]');
                if (currentAuxiliary && fetchedAuxiliary) {
                    appendMissingChildren(
                        currentAuxiliary,
                        fetchedAuxiliary,
                        '[data-fj-infinite-search-auxiliary-item]',
                        'data-fj-infinite-search-auxiliary-key'
                    );
                }

                const currentPagination = root.querySelector<HTMLElement>('[data-fj-infinite-search-pagination]');
                const fetchedPagination = fetchedRoot.querySelector<HTMLElement>('[data-fj-infinite-search-pagination]');
                if (currentPagination && fetchedPagination) {
                    currentPagination.replaceWith(fetchedPagination.cloneNode(true));
                } else {
                    currentPagination?.remove();
                }
                currentPageUrl = nextUrl;
                options.afterAppend?.(root);
                bindNext();
            } catch {
                trigger.removeAttribute('aria-disabled');
                trigger.dataset.fjInfiniteSearchFailed = 'true';
                trigger.textContent = 'Load more jobs';
            } finally {
                loading = false;
                root.setAttribute('aria-busy', 'false');
            }
        };

        root.addEventListener('click', (event) => {
            const target = event.target instanceof runtimeWindow.Element
                ? event.target.closest<HTMLAnchorElement>('[data-fj-infinite-search-next]')
                : null;
            if (!target) return;
            event.preventDefault();
            void loadNext(target);
        });
        bindNext();
    });
}
